<template>
  <BaseModal :state="state" :open="open" :close="onClose">
    <template #content>
      <BaseLoader v-if="loading.acompanhamentos" />

      <template v-else>
        <div
          class="flex border-b border-b-solid border-b-neutral-100/10 flex-nowrap items-center"
        >
          <q-tabs v-model="tab" class="" active-color="primary-pure">
            <q-tab name="tab1">
              <p class="!text-headline-3">
                {{ t('newContact') }}
              </p>
            </q-tab>

            <q-tab v-if="acompanhamentos?.length" name="tab2">
              <p class="!text-headline-3">
                {{ t('historyLog') }}
              </p>
            </q-tab>
          </q-tabs>
          <div class="flex-1" />
        </div>

        <q-tab-panels
          v-model="tab"
          animated
          class="flex flex-col flex-1 pb-80 overflow-hidden CustomTabPanels"
        >
          <q-tab-panel name="tab1">
            <q-form ref="formRef" no-error-focus>
              <div class="p-24">
                <h3 class="text-headline-3 text-neutral-100">
                  {{ t('question') }}: {{ pergunta?.pergunta }}
                </h3>
              </div>

              <q-separator />

              <QCardSection class="flex flex-col gap-16 p-24">
                <h3 class="text-headline-3 text-neutral-100">
                  {{ t('addAComment') }}:
                </h3>

                <OSelect
                  :model-value="model.nivel_maturidade"
                  :label="t('selectTheMaturity')"
                  :options="alternativas"
                  size="md"
                  multiple
                  emit-value
                  map-options
                  option-label="titulo"
                  option-value="id"
                  :rules="[(value) => !!value]"
                  @update:model-value="
                    (value) => (model.nivel_maturidade = value)
                  "
                />

                <OInput
                  v-model="model.contato"
                  size="lg"
                  type="textarea"
                  stack-label
                  :label="t('comment')"
                  :placeholder="t('writeHere')"
                  class="showPlaceholderOnStack"
                  :rules="[(value) => value?.length > 0]"
                />
              </QCardSection>

              <q-separator />

              <QCardSection class="p-24">
                <FileUploader
                  accept=""
                  container-class="h-96 p-12 flex flex-col items-center justify-center gap-6 rounded-md border border-dashed border-neutral-100/10 hover:border-neutral-100/30 bg-card-300 transition-all cursor-pointer"
                  @change="(value) => (model.files = value)"
                >
                  <p class="text-paragraph-4 text-neutral-70">
                    {{ t('attachHereYourFile') }}
                  </p>
                  <div
                    class="h-32 grid place-items-center bg-white border border-neutral-100/10 rounded-md px-10 mt-8"
                  >
                    <p class="text-headline-4 text-neutral-100">
                      {{ t('selectFile') }}
                    </p>
                  </div>
                </FileUploader>
              </QCardSection>
            </q-form>
          </q-tab-panel>

          <q-tab-panel name="tab2">
            <div class="p-24">
              <h3 class="text-headline-3 text-neutral-100">
                {{ t('question') }}: {{ pergunta?.pergunta }}
              </h3>
            </div>

            <q-separator />

            <template
              v-for="acompanhamento in acompanhamentos"
              :key="acompanhamento.id"
            >
              <QCardSection class="flex flex-col gap-16 p-24">
                <div class="flex flex-col gap-12">
                  <p class="text-paragraph-3 text-neutral-100">
                    {{ t('lastContact') }}:
                    <span class="text-headline-3">
                      {{ acompanhamento.data_criacao_translated }}
                    </span>
                  </p>
                  <p class="text-paragraph-3 text-neutral-100">
                    {{ t('currentMaturityLevel') }}:
                    <span class="text-headline-3">
                      {{ nivelMaturidadeTitulo(acompanhamento) }}
                    </span>
                  </p>
                </div>

                <OInput
                  v-model="acompanhamento.contato"
                  size="lg"
                  type="textarea"
                  :label="t('comment')"
                  disabled
                  readonly
                  class="label-transparent readonly"
                />
              </QCardSection>

              <template v-if="acompanhamento.arquivo">
                <QCardSection class="px-24 !pt-0">
                  <p class="text-caps-3 text-neutral-70">
                    {{ t('attachment') }}
                  </p>

                  <q-list class="w-full space-y-8 mt-8">
                    <OFileDownload
                      :file="acompanhamento.file_data"
                      :name="acompanhamento.file_data.nome_read"
                    />
                  </q-list>
                </QCardSection>
              </template>

              <q-separator />
            </template>
          </q-tab-panel>
        </q-tab-panels>

        <div
          class="w-full h-80 absolute bottom-0 left-0 z-10 bg-card-100 border-t border-neutral-100/10 p-16"
        >
          <div class="flex items-center justify-between gap-8">
            <OButton
              secondary
              size="md"
              :href="`/${context.assessment.slug}/acompanhamento_detalhes/?pergunta=${pergunta?.id}`"
              :label="t('goToSummary')"
              class="text-headline-3"
            />
            <OButton
              v-if="tab === 'tab1'"
              primary
              size="md"
              :label="t('saveAction')"
              class="text-headline-3 !min-w-120"
              @click="onSubmit"
            />
          </div>
        </div>
      </template>

      <BaseInnerLoader v-if="loading.enviandoAcompanhamento" />
    </template>
  </BaseModal>
</template>

<script setup>
import { inject, onMounted, ref } from 'vue'
import { NotifyError } from 'js/vue/boot/Notify'
import { useI18n } from 'vue-i18n'
import BaseInnerLoader from 'components/Loader/BaseInnerLoader.vue'
import BaseLoader from 'components/Loader/BaseLoader.vue'
import BaseModal from 'components/Modal/BaseModal.vue'
import FileUploader from 'components/FileUploader/FileUploader.vue'
import OButton from 'components/Button/OButton.vue'
import OFileDownload from 'components/FileUploader/OFileDownload.vue'
import OInput from 'components/Input/OInput.vue'
import OSelect from 'components/Select/OSelect.vue'
import useModal from 'composables/useModal'
import usePerguntas from 'composables/usePerguntas'

const { t } = useI18n()
const context = inject('context')

const modalAcompanhamento = useModal()

const { state, open, close, setState } = modalAcompanhamento

const { initialRequestsAcompanhamento, acompanhamentos, loading } =
  usePerguntas()

const props = defineProps({
  pergunta: Object,
  alternativas: Array,
})

const tab = ref('tab1')
const formRef = ref(null)
const acompanhamentoId = ref(props.pergunta.acompanhamento)

const model = ref({
  nivel_maturidade: [],
  contato: '',
  files: [],
  acompanhamento: acompanhamentoId.value,
})

function nivelMaturidadeTitulo(acompanhamento) {
  return acompanhamento.niveis_maturidade_titulo.join(', ')
}

async function onSubmit() {
  if (!model.value.nivel_maturidade.length) {
    NotifyError(t('selectTheMaturity'))
    return
  }
  if (!model.value.contato) {
    NotifyError(t('commentSectionIs'))
    return
  }
  const valid = await formRef.value.validate()
  if (!valid) return

  emit('submit', model.value)
}

onMounted(() => {
  if (!acompanhamentoId.value) return
  initialRequestsAcompanhamento(acompanhamentoId.value)
})

const onClose = () => {
  close()
}

setState({
  header: { title: t('summary') },
  style: { width: '50rem', minHeight: '80vh' },
})

const emit = defineEmits(['submit'])

defineExpose({
  state,
  open,
  close,
  setState,
})
</script>

<style lang="scss" scoped>
.CustomTabPanels :deep(.q-panel) {
  flex: 1;
}
</style>
